import React from 'react';
import {Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {SeatingPlan} from "../../api/Model";
import {useSeatingPlanStore} from "../../api/SeatingPlanStore";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {SeatingPlanFormDialog} from "./SeatingPlanFormDialog";
import {SeatingPlanPane} from "./SeatingPlanPane";
import {useNavigate} from "react-router-dom";
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {StatusQuoButton} from "./StatusQuoButton";

export default function SeatingPlanListPage() {
    const {
        remoteValue: seatingPlans,
        createPlan,
        updatePlan,
        deletePlan,
    } = useSeatingPlanStore();
    const navigate = useNavigate();
    const pageUrl = usePageUrl();
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});

    const handleCreate = useFormDialogAction({
        title: "Create Seating Plan",
        description: "Enter the details for the new seating plan.",
        submitButtonText: 'Create',
        renderForm: SeatingPlanFormDialog,
        action: (openDialog) => openDialog({
            initialValue: {
                name: '',
                description: null,
            },
            onSubmit: async (form) => {
                const plan = await createPlan(form);
                navigate(pageUrl(AppPages.seatingPlan, {planId: plan.id}));
                return plan;
            },
            successMessage: 'Created new seating plan',
            errorMessage: 'Failed to create seating plan. Please try again later.',
        })
    }, [createPlan, pageUrl]);

    const handleUpdate = useFormDialogAction({
        title: "Edit Seating Plan",
        description: "Update the seating plan information below.",
        submitButtonText: 'Update',
        renderForm: SeatingPlanFormDialog,
        action: (openDialog, plan: SeatingPlan) => openDialog({
            initialValue: {
                name: plan.name,
                description: plan.description,
            },
            onSubmit: form => updatePlan(plan, form),
            successMessage: form => `Updated seating plan ${form.name}.`,
            errorMessage: 'Failed to update seating plan. Please try again later.',
        }),
    }, [updatePlan]);

    const handleDelete = useDeleteAction({
        description: 'Are you sure you want to delete this seating plan? This action cannot be undone.',
        action: deletePlan,
        successMessage: 'Deleted seating plan',
        failureMessage: 'Failed to delete seating plan. Please try again later.',
    }, [deletePlan]);

    return (
        <Page
            header="Seating Plans"
            counter={<StorePageCount value={seatingPlans}/>}
            description="Manage seating plan changes."
            actions={canAdmin &&
                <Stack direction="row" gap={2}>
                    <StatusQuoButton/>
                    <Button variant="contained" color="primary" startIcon={<AddIcon/>} onClick={handleCreate}>
                        Create Plan
                    </Button>
                </Stack>
            }
        >
            <LoadingData
                value={seatingPlans}
                emptyMessage="No seating plans found."
                defaultErrorMessage="Failed to load seating plans. Please try again later."
            >{resolved => (
                <Stack spacing={2} direction="column">
                    {resolved.map(plan => (
                        <SeatingPlanPane
                            key={plan.id}
                            seatingPlan={plan}
                            onEdit={canAdmin ? handleUpdate : undefined}
                            onDelete={canAdmin ? handleDelete : undefined}
                        />
                    ))}
                </Stack>
            )}</LoadingData>
        </Page>
    );
}
